import React, { useState, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ReactGA from "react-ga4";

import "./styles/globals.css";
import "./styles/components.css";

import imageData from "./data/imageData";
import useGameState from "./hooks/useGameState";
import useRoll from "./hooks/useRoll";
import useAuth from "./useAuth";

import { Sparks, Confetti, Toast } from "./components/ui/index";
import EpicReveal    from "./components/game/EpicReveal";
import BulkRoll      from "./components/game/BulkRoll";
import SpinWheel     from "./components/game/SpinWheel";
import HamsterDetail from "./components/collection/HamsterDetail";
import ShareCard     from "./components/collection/ShareCard";
import Leaderboard   from "./components/social/Leaderboard";
import TopTrumps     from "./components/social/TopTrumps";
import Shop          from "./components/menus/Shop";
import Achievements  from "./components/menus/Achievements";
import Missions, { loadMissionProgress, saveMissionProgress, getTodaysMissions,
                   checkMissionComplete, loadWeeklyProgress, saveWeeklyProgress,
                   updateWeeklyProgress } from "./components/menus/Missions";
import Stats         from "./components/menus/Stats";
import Prestige      from "./components/menus/Prestige";
import Settings      from "./components/menus/Settings";
import DailyStreak   from "./components/menus/DailyStreak";
import SavePrompt, { AuthModal, ConflictModal } from "./SavePrompt";

import { playClickSound, playCloseSound, playPurchaseSound,
         playMissionSound, playPrestigeSound,
         playStreakSound, setMusicVolume } from "./Sounds";
import { RARITY_ORDER, ROLL_COST, COIN_VALUES, EPIC_RARITIES } from "./constants";
import { rarityHex } from "./utils";

ReactGA.initialize("G-W9M76MPZB6");

/* ─── HELPERS ────────────────────────────────────────────────── */
function ActiveBoosts({ boosts }) {
  const chips = [];
  if (boosts.lucky_charm)         chips.push("🍀 Lucky Charm");
  if (boosts.rare_surge)          chips.push("⚡ Rare Surge");
  if (boosts.epic_guarantee)      chips.push("💜 Epic Guarantee");
  if (boosts.jackpot)             chips.push("💎 Jackpot");
  if (boosts.hot_streak > 0)      chips.push(`🔥 Hot Streak ×${boosts.hot_streak}`);
  if (boosts.coin_multiplier > 0) chips.push(`💰 2× Coins ×${boosts.coin_multiplier}`);
  if (boosts.hamster_magnet > 0)  chips.push(`🧲 Magnet ×${boosts.hamster_magnet}`);
  if (!chips.length) return null;
  return (
    <div className="active-boosts">
      {chips.map((c, i) => <span key={i} className="boost-chip">{c}</span>)}
    </div>
  );
}

function PityBars({ pity }) {
  const THRESHOLDS = { Rare: 50, Epic: 100, Legendary: 200 };
  return (
    <div className="pity-row">
      {Object.entries(pity).map(([tier, count]) => (
        <div key={tier} className="pity-item">
          <span style={{ color: rarityHex(tier) }}>{tier}</span>
          <div className="pity-bar-wrap">
            <div className="pity-bar-fill" style={{
              width: `${Math.min(count / THRESHOLDS[tier], 1) * 100}%`,
              background: rarityHex(tier),
            }} />
          </div>
          <span>{count}/{THRESHOLDS[tier]}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── APP ────────────────────────────────────────────────────── */
export default function App() {
  const auth = useAuth();

  const gs = useGameState({
    onHighValueAction: () => {
      if (auth.user) auth.saveToCloud(auth.user.uid, true);
    },
  });

  const roll = useRoll({
    coins: gs.coins, boosts: gs.boosts, inventory: gs.inventory,
    pity: gs.pity, prestige: gs.prestige, rollCount: gs.rollCount,
    totalCoins: gs.totalCoins, unlockedAch: gs.unlockedAch,
    cutscenes: gs.cutscenes, soundEnabled: gs.soundEnabled,
    applyRollResult: gs.applyRollResult, spendCoins: gs.spendCoins,
    addCoins: gs.addCoins, applyBoost: gs.applyBoost,
    unlockAchievements: gs.unlockAchievements,
    checkAchievements: gs.checkAchievements, buildAchState: gs.buildAchState,
    setBoosts: gs.setBoosts,
  });

  const [showShop,        setShowShop]        = useState(false);
  const [showAch,         setShowAch]         = useState(false);
  const [showMissions,    setShowMissions]    = useState(false);
  const [showStats,       setShowStats]       = useState(false);
  const [showPrestige,    setShowPrestige]    = useState(false);
  const [showSettings,    setShowSettings]    = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBattle,      setShowBattle]      = useState(false);
  const [showShareCard,   setShowShareCard]   = useState(false);
  const [showStreak,      setShowStreak]      = useState(false);
  const [showMoreNav,     setShowMoreNav]     = useState(false);
  const [showHistory,     setShowHistory]     = useState(false);
  const [showAuthModal,   setShowAuthModal]   = useState(false);
  const [signingIn,       setSigningIn]       = useState(false);
  const [selectedHamster, setSelectedHamster] = useState(null);
  const [confetti,        setConfetti]        = useState(false);
  const [newAchBadge,     setNewAchBadge]     = useState(false);
  const [missionProgress, setMissionProgress] = useState(() => loadMissionProgress());

  const [searchQuery,    setSearchQuery]    = useState("");
  const [selectedRarity, setSelectedRarity] = useState("");
  const [sortOrder,      setSortOrder]      = useState("newest");
  const [showFavOnly,    setShowFavOnly]    = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowStreak(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (roll.newAchToast.length) { setNewAchBadge(true); setConfetti(true); }
  }, [roll.newAchToast]);
  useEffect(() => { if (confetti) { const t = setTimeout(() => setConfetti(false), 3500); return () => clearTimeout(t); } }, [confetti]);
  useEffect(() => { setMusicVolume(gs.musicVolume / 100); }, [gs.musicVolume]);

  const handleBuy = useCallback((item) => {
    const cost = item.cost;
    if (gs.coins < cost) return;
    gs.spendCoins(cost);
    if (gs.soundEnabled) playPurchaseSound?.();
    if (item.id === "mystery_box")    { roll.doMysteryBox(); }
    else if (item.id === "bulk_roll") { roll.doBulkRoll(10); }
    else if (item.id === "auto_roll") { roll.doAutoRoll(10); }
    else { gs.applyBoost(item.id); }
    const mp = loadMissionProgress();
    mp.progress.coinsSpent = (mp.progress.coinsSpent || 0) + cost;
    saveMissionProgress(mp);
    setMissionProgress(loadMissionProgress());
  }, [gs, roll]);

  const handleMissionClaim = useCallback((mission) => {
    gs.addCoins(mission.reward);
    if (gs.soundEnabled) playMissionSound?.();
    const mp = loadMissionProgress();
    mp.claimed[mission.id] = true;
    saveMissionProgress(mp);
    setMissionProgress(loadMissionProgress());
  }, [gs]);

  const handleWeeklyClaim = useCallback((weekly) => {
    gs.addCoins(weekly.reward);
    if (gs.soundEnabled) playMissionSound?.();
    const wp = loadWeeklyProgress();
    wp.claimed = true;
    saveWeeklyProgress(wp);
  }, [gs]);

  const handleStreakClaim = useCallback(({ coins, milestone }) => {
    gs.addCoins(coins);
    if (gs.soundEnabled) playStreakSound?.();
    if (milestone) gs.applyBoost(milestone.boost);
  }, [gs]);

  const handleBattleWin = useCallback(() => {
    updateWeeklyProgress("battleWins", 1);
  }, []);

  const handleSignIn = useCallback(async () => {
    setSigningIn(true);
    await auth.signInWithGoogle();
    // Don't reload here — onAuthStateChanged fires, loadFromCloud hydrates
    // localStorage, then reloads the page itself once data is ready
    setSigningIn(false);
    setShowAuthModal(false);
  }, [auth]);

  const handlePrestige = useCallback(() => {
    gs.doPrestige();
    if (gs.soundEnabled) playPrestigeSound?.();
    setShowPrestige(false);
  }, [gs]);

  const handleShatter = useCallback((hamster, dupeCount, payout) => {
    gs.shatterDupes(hamster, dupeCount, payout);
    setSelectedHamster(null);
  }, [gs]);

  const uniqueInventory = (() => {
    const seen = new Set();
    return gs.inventory.filter(item => {
      if (seen.has(item.name)) return false;
      seen.add(item.name); return true;
    });
  })();

  const filtered = uniqueInventory.filter(item => {
    if (showFavOnly && !gs.favourites.includes(item.name)) return false;
    if (selectedRarity && item.rarity !== selectedRarity) return false;
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sortOrder === "rarest")  return RARITY_ORDER.indexOf(b.rarity) - RARITY_ORDER.indexOf(a.rarity);
    if (sortOrder === "common")  return RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity);
    if (sortOrder === "alpha")   return a.name.localeCompare(b.name);
    if (sortOrder === "dupes")   return (gs.pullCounts[b.name] || 1) - (gs.pullCounts[a.name] || 1);
    return gs.inventory.map(i => i.name).lastIndexOf(b.name) - gs.inventory.map(i => i.name).lastIndexOf(a.name);
  });

  const completionPct = Math.round((uniqueInventory.length / imageData.length) * 100);
  const broke = gs.coins < ROLL_COST;

  const missionBadge = (() => {
    const missions = getTodaysMissions();
    const { progress, claimed } = missionProgress;
    return missions.filter(m => !claimed[m.id] && checkMissionComplete(m, progress)).length;
  })();

  return (
    <Router>
      <div className="app" data-theme={gs.theme}>
        <Sparks />
        {confetti && <Confetti />}

        <Toast messages={roll.newAchToast} onDone={() => roll.setNewAchToast([])} />
        <Toast messages={roll.toastMsgs}   onDone={() => roll.setToastMsgs([])} />

        {showStreak && <DailyStreak onClaim={handleStreakClaim} onClose={() => setShowStreak(false)} />}

        {/* ── AUTH ── */}
        {auth.conflictData && (
          <ConflictModal
            conflictData={auth.conflictData}
            onChoose={(choice) => auth.resolveConflictChoice(choice, auth.user?.uid)}
          />
        )}
        {showAuthModal && !auth.user && (
          <AuthModal
            onSignIn={handleSignIn}
            onClose={() => setShowAuthModal(false)}
            loading={signingIn}
          />
        )}
        {!auth.user && (
          <SavePrompt
            rollCount={gs.rollCount}
            onSignIn={() => setShowAuthModal(true)}
            loading={signingIn}
          />
        )}

        {roll.showEpic && roll.lastRoll && (
          <EpicReveal image={roll.lastRoll} rarity={roll.lastRoll.rarity} onDone={() => roll.setShowEpic(false)} />
        )}

        {roll.showSpin && roll.pendingRoll && (
          <SpinWheel
            targetCard={roll.pendingRoll}
            isDupe={roll.pendingIsDupe}
            inventory={gs.inventory}
            onDone={() => {
              roll.setShowSpin(false);
              const showCutscene = gs.cutscenes?.[roll.pendingRoll.rarity] !== false;
              if (EPIC_RARITIES.includes(roll.pendingRoll.rarity) && showCutscene) roll.setShowEpic(true);
              roll.setPendingRoll(null);
            }}
          />
        )}

        {roll.bulkResults && (
          <BulkRoll results={roll.bulkResults} onDone={() => roll.setBulkResults(null)} />
        )}

        {showShop        && <Shop coins={gs.coins} boosts={gs.boosts} onBuy={handleBuy} onClose={() => { playCloseSound(); setShowShop(false); }} />}
        {showAch         && <Achievements unlockedAch={gs.unlockedAch} onClose={() => { playCloseSound(); setShowAch(false); setNewAchBadge(false); }} />}
        {showMissions    && <Missions missionProgress={missionProgress} onClaim={handleMissionClaim} onWeeklyClaim={handleWeeklyClaim} onClose={() => { playCloseSound(); setShowMissions(false); }} />}
        {showStats       && <Stats onClose={() => { playCloseSound(); setShowStats(false); }} inventory={gs.inventory} rollCount={gs.rollCount} totalCoins={gs.totalCoins} coins={gs.coins} prestige={gs.prestige} pity={gs.pity} rollHistory={gs.rollHistory} showHistory={showHistory} onShowHistory={() => setShowHistory(true)} onCloseHistory={() => setShowHistory(false)} />}
        {showPrestige    && <Prestige prestige={gs.prestige} inventory={gs.inventory} rollCount={gs.rollCount} onConfirm={handlePrestige} onClose={() => { playCloseSound(); setShowPrestige(false); }} />}
        {showSettings    && <Settings theme={gs.theme} soundEnabled={gs.soundEnabled} volume={gs.volume} onThemeToggle={gs.toggleTheme} onSoundToggle={gs.toggleSound} onVolumeChange={gs.handleVolume} musicOn={gs.musicOn} musicVolume={gs.musicVolume} onMusicToggle={gs.toggleMusic} onMusicVolumeChange={gs.handleMusicVol} cutscenes={gs.cutscenes} onCutsceneToggle={gs.toggleCutscene} wheelEnabled={gs.wheelEnabled} onWheelToggle={gs.toggleWheel} authUser={auth.user} onSignIn={() => { setShowSettings(false); setShowAuthModal(true); }} onSignOut={auth.signOut} onClose={() => { playCloseSound(); setShowSettings(false); }} />}
        {showLeaderboard && <Leaderboard rollCount={gs.rollCount} inventory={gs.inventory} totalCoins={gs.totalCoins} prestige={gs.prestige} battleTrophies={parseInt(localStorage.getItem("battleTrophies") || "0", 10)} onClose={() => { playCloseSound(); setShowLeaderboard(false); }} />}
        {showBattle      && <TopTrumps inventory={gs.inventory} onEarnCoins={gs.addCoins} onBattleResult={() => {}} onBattleWin={handleBattleWin} onClose={() => { playCloseSound(); setShowBattle(false); }} />}
        {showShareCard   && <ShareCard inventory={gs.inventory} rollCount={gs.rollCount} totalCoins={gs.totalCoins} prestige={gs.prestige} onClose={() => { playCloseSound(); setShowShareCard(false); }} />}
        {selectedHamster && <HamsterDetail hamster={selectedHamster} pullStats={gs.pullStats} favourites={gs.favourites} onToggleFavourite={gs.toggleFavourite} onShatter={handleShatter} onClose={() => setSelectedHamster(null)} />}

        <Routes>
          <Route path="*" element={
            <div className="container">
              <h1>🐹 Hamster Gambler</h1>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", marginBottom: 20 }}>
                Collect · Battle · Prestige
                {gs.prestige > 0 && <span style={{ color: "var(--neon-gold)", marginLeft: 8 }}>⭐ Prestige {gs.prestige}</span>}
              </p>

              {/* ── ACCOUNT BUTTON (top right) ── */}
              <div className="account-btn-wrap">
                {auth.user ? (
                  <>
                    {auth.saveStatus === "saving" && <span className="save-indicator saving">☁️</span>}
                    {auth.saveStatus === "saved"  && <span className="save-indicator saved">☁️</span>}
                    {auth.saveStatus === "error"  && <span className="save-indicator error">⚠️</span>}
                    <button
                      className="account-btn"
                      onClick={() => setShowSettings(true)}
                      title={auth.user.displayName || auth.user.email}
                    >
                      {auth.user.photoURL
                        ? <img src={auth.user.photoURL} alt="avatar" className="account-btn-avatar" referrerPolicy="no-referrer" />
                        : <span>👤</span>
                      }
                    </button>
                  </>
                ) : (
                  <button className="account-btn account-btn-guest" onClick={() => setShowAuthModal(true)}>
                    ☁️ Sign in
                  </button>
                )}
              </div>

              {/* ── COIN BAR ── */}
              <div className={`coin-bar${broke ? " coin-bar-broke" : ""}`}>
                🪙 {gs.coins.toLocaleString()}
                <span className="coin-bar-roll-cost">· {ROLL_COST} per roll</span>
              </div>

              <ActiveBoosts boosts={gs.boosts} />
              <PityBars pity={gs.pity} />

              {/* ── ROLL BUTTON ── */}
              <div className="roll-section">
                <button
                  className="roll-btn"
                  onClick={() => roll.doRoll(gs.wheelEnabled)}
                  disabled={broke || roll.rolling || roll.cooldown}
                >
                  {roll.rolling || roll.cooldown ? "Rolling…" : broke ? "🪙 Need more coins" : "🎲 Roll!"}
                </button>
              </div>

              {/* ── BROKE PANEL ── */}
              {broke && (
                <div className="broke-panel">
                  <div className="broke-title">💸 You're out of coins!</div>
                  <div className="broke-tip">You need at least <strong>🪙 {ROLL_COST} coins</strong> to roll. Here's how to earn more:</div>
                  <div className="broke-ways">
                    <div className="broke-way" onClick={() => { playClickSound(); setShowMissions(true); }}>
                      <span className="broke-way-icon">📋</span>
                      <div><div className="broke-way-title">Complete Missions</div><div className="broke-way-sub">Daily missions reward 40–350 coins each</div></div>
                      <span className="broke-way-arrow">→</span>
                    </div>
                    <div className="broke-way" onClick={() => setShowStreak(true)}>
                      <span className="broke-way-icon">🔥</span>
                      <div><div className="broke-way-title">Claim Daily Streak</div><div className="broke-way-sub">Free coins every day you log in</div></div>
                      <span className="broke-way-arrow">→</span>
                    </div>
                    <div className="broke-way" onClick={() => { playClickSound(); setShowBattle(true); }}>
                      <span className="broke-way-icon">⚔️</span>
                      <div><div className="broke-way-title">Win a Battle</div><div className="broke-way-sub">Top Trumps wins reward coins</div></div>
                      <span className="broke-way-arrow">→</span>
                    </div>
                    {gs.inventory.length > 0 && (
                      <div className="broke-way" onClick={() => setSelectedHamster(uniqueInventory[0])}>
                        <span className="broke-way-icon">💎</span>
                        <div><div className="broke-way-title">Shatter Duplicates</div><div className="broke-way-sub">Turn spare hamsters into coins</div></div>
                        <span className="broke-way-arrow">→</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── LAST ROLL CARD ── */}
              {roll.lastRoll && (() => {
                const colour = rarityHex(roll.lastRoll.rarity);
                const isDupe = roll.lastIsDupe;
                const earned = COIN_VALUES[roll.lastRoll.rarity] || 0;
                return (
                  <div className="roll-card" style={{ borderColor: colour }}>
                    <img src={roll.lastRoll.image} alt={roll.lastRoll.name} style={{ borderColor: colour }} />
                    <div className="roll-card-name">{roll.lastRoll.name}</div>
                    <div className="roll-card-rarity" style={{ color: colour }}>✦ {roll.lastRoll.rarity}</div>
                    <div className="roll-card-coins">+🪙 {isDupe ? earned : earned * 2}</div>
                    <div className="roll-card-new">{isDupe ? "♻️ Duplicate" : "✨ New!"}</div>
                  </div>
                );
              })()}

              {/* ── NAV ── */}
              <nav className="bottom-nav">
                <button className="nav-btn" onClick={() => { playClickSound(); setShowShop(true); }}>
                  <span className="nav-icon">🛒</span><span className="nav-label">Shop</span>
                </button>
                <button className="nav-btn" onClick={() => { playClickSound(); setShowMissions(true); }}>
                  <span className="nav-icon">📋</span>
                  <span className="nav-label">Missions</span>
                  {missionBadge > 0 && <span className="nav-badge">{missionBadge}</span>}
                </button>
                <button className="nav-btn" onClick={() => { playClickSound(); setShowAch(true); }}>
                  <span className="nav-icon">🏆</span>
                  <span className="nav-label">Goals</span>
                  {newAchBadge && <span className="nav-badge new">!</span>}
                </button>
                <button className="nav-btn" onClick={() => { playClickSound(); setShowLeaderboard(true); }}>
                  <span className="nav-icon">🏅</span><span className="nav-label">Ranks</span>
                </button>
                <button className="nav-btn nav-btn-battle" onClick={() => { playClickSound(); setShowBattle(true); }}>
                  <span className="nav-icon">⚔️</span><span className="nav-label">Battle</span>
                </button>
                <div className="nav-more-wrap">
                  <button className={`nav-btn nav-btn-more${showMoreNav ? " active" : ""}`} onClick={() => setShowMoreNav(v => !v)}>
                    <span className="nav-icon more-icon-dots"><span/><span/><span/></span>
                    <span className="nav-label">More</span>
                  </button>
                  {showMoreNav && (
                    <>
                      <div className="nav-more-backdrop" onClick={() => setShowMoreNav(false)} />
                      <div className="nav-more-dropdown">
                        <div className="nav-more-header">Quick Access</div>
                        <button className="nav-more-item" onClick={() => { playClickSound(); setShowStats(true); setShowMoreNav(false); }}>
                          <span className="nav-more-icon">📊</span>
                          <span className="nav-more-text"><strong>Stats</strong><small>Your roll history</small></span>
                        </button>
                        <button className="nav-more-item nav-more-prestige" onClick={() => { playClickSound(); setShowPrestige(true); setShowMoreNav(false); }}>
                          <span className="nav-more-icon">⭐</span>
                          <span className="nav-more-text"><strong>Prestige</strong><small>Reset for bonuses</small></span>
                        </button>
                        <div className="nav-more-divider"/>
                        <button className="nav-more-item" onClick={() => { playClickSound(); setShowSettings(true); setShowMoreNav(false); }}>
                          <span className="nav-more-icon">⚙️</span>
                          <span className="nav-more-text"><strong>Settings</strong><small>Theme · Sound · More</small></span>
                        </button>
                        <button className="nav-more-item nav-more-share" onClick={() => { playClickSound(); setShowShareCard(true); setShowMoreNav(false); }}>
                          <span className="nav-more-icon">📸</span>
                          <span className="nav-more-text"><strong>Share Card</strong><small>Show off your pulls</small></span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </nav>

              {/* ── COLLECTION ── */}
              <section className="collection-section">
                <div className="collection-header">
                  <h2>Your Collection</h2>
                  <div className="collection-completion">
                    <span>{uniqueInventory.length}</span>/{imageData.length}
                    <span className="completion-pct"> · {completionPct}%</span>
                  </div>
                </div>

                <div className="controls-row">
                  <input type="text" placeholder="🔍 Search…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="search-bar" />
                  <select value={selectedRarity} onChange={e => setSelectedRarity(e.target.value)} className="styled-select">
                    <option value="">All rarities</option>
                    {RARITY_ORDER.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="styled-select">
                    <option value="newest">✨ Newest</option>
                    <option value="rarest">👑 Rarest first</option>
                    <option value="common">🌱 Common first</option>
                    <option value="alpha">🔤 A–Z</option>
                    <option value="dupes">♻️ Most dupes</option>
                  </select>
                  <button className={`fav-filter-btn${showFavOnly ? " active" : ""}`} onClick={() => setShowFavOnly(v => !v)}>
                    {showFavOnly ? "⭐ Favs" : "☆ Favs"}
                  </button>
                </div>

                <div className="inventory-list">
                  {filtered.map((item, index) => {
                    const colour = rarityHex(item.rarity);
                    const count  = gs.pullCounts[item.name] || 1;
                    const isFav  = gs.favourites.includes(item.name);
                    return (
                      <div key={index} className={`inventory-item${isFav ? " is-favourite" : ""}`}
                        style={{ animationDelay: `${index * 0.04}s`, borderTop: `3px solid ${colour}`, color: colour }}
                        onClick={() => setSelectedHamster(item)}>
                        {count > 1 && <div className="dupe-count">×{count}</div>}
                        <button className="fav-btn" onClick={e => { e.stopPropagation(); gs.toggleFavourite(item.name); }}>
                          {isFav ? "⭐" : "☆"}
                        </button>
                        <img src={item.image} alt={item.name} style={{ borderColor: `${colour}55` }} />
                        <p>{item.name}</p>
                        <p className="rarity" style={{ color: colour, fontWeight: 800 }}>{item.rarity}</p>
                      </div>
                    );
                  })}

                  {filtered.length === 0 && uniqueInventory.length > 0 && (
                    <p style={{ color: "var(--text-muted)", gridColumn: "1/-1", textAlign: "center", padding: "20px 0" }}>
                      No hamsters match your search
                    </p>
                  )}

                  {!searchQuery && !selectedRarity && !showFavOnly && (() => {
                    const ownedNames = new Set(gs.inventory.map(i => i.name));
                    const unowned = imageData.filter(i => !ownedNames.has(i.name));
                    if (!unowned.length) return null;
                    return unowned.map((item, idx) => {
                      const colour = rarityHex(item.rarity);
                      return (
                        <div key={"ghost-" + idx} className="inventory-item inventory-item-ghost"
                          style={{ borderTop: `3px solid ${colour}44` }} title="Roll to discover!">
                          <div className="ghost-img-wrap">
                            <img src={item.image} alt="???" className="ghost-img" />
                            <div className="ghost-lock">🔒</div>
                          </div>
                          <p className="ghost-name">???</p>
                          <p className="rarity" style={{ color: `${colour}88`, fontWeight: 800 }}>{item.rarity}</p>
                        </div>
                      );
                    });
                  })()}

                  {gs.inventory.length === 0 && (
                    <p style={{ color: "#9980c4", marginTop: 16, fontSize: "1rem", gridColumn: "1/-1" }}>
                      No hamsters yet — start rolling!
                    </p>
                  )}
                </div>
              </section>
            </div>
          }/>
        </Routes>
      </div>
    </Router>
  );
}